// File: src/utils/notificationDisplay.js

class NotificationDisplay {
    constructor() {
        this.isServiceWorkerSupported = 'serviceWorker' in navigator && 'showNotification' in ServiceWorkerRegistration.prototype;
        this.permission = Notification.permission;
    }

    // Check if notifications are supported and permitted
    async checkPermission() {
        if (!this.isServiceWorkerSupported) {
            return { supported: false, permission: 'denied' };
        }

        let permission = Notification.permission;
        
        if (permission === 'default') {
            permission = await Notification.requestPermission();
        }

        this.permission = permission;
        return { 
            supported: true, 
            permission: permission,
            granted: permission === 'granted' 
        };
    }

    // Show notification using service worker
    async showNotification(title, body, options = {}) {
        try {
            // Check permission first
            const permissionCheck = await this.checkPermission();
            
            if (!permissionCheck.supported) {
                console.warn('Notifications not supported');
                return false;
            }

            if (!permissionCheck.granted) {
                console.warn('Notification permission not granted');
                return false;
            }

            // Wait for service worker to be ready
            const registration = await navigator.serviceWorker.ready;
            
            const notificationOptions = {
                body: body,
                icon: '/icon.png',
                badge: '/badge.png',
                tag: 'app-notification',
                requireInteraction: false,
                vibrate: [200, 100, 200],
                timestamp: Date.now(),
                ...options
            };

            await registration.showNotification(title, notificationOptions);
            return true;

        } catch (error) {
            console.error('Error showing notification:', error);
            return false;
        }
    }

    // Show success notification
    async showSuccess(title, message, options = {}) {
        return await this.showNotification(title, message, {
            icon: '/logo192.png',
            tag: 'success',
            ...options
        });
    }

    // Show error notification
    async showError(title, message, options = {}) {
        return await this.showNotification(title, message, {
            icon: '/icon.png',
            tag: 'error',
            requireInteraction: true,
            ...options
        });
    }

    // Show login success notification
    async showLoginSuccess(userName) {
        return await this.showSuccess(
            'Đăng nhập thành công',
            `Chào mừng ${userName}!`,
            {
                tag: 'login-success',
                actions: [
                    {
                        action: 'open_app',
                        title: 'Mở ứng dụng'
                    }
                ]
            }
        );
    }

    // Show shipment notification
    async showShipmentUpdate(shipmentId, message, options = {}) {
        return await this.showNotification(
            'Cập nhật chuyến xe',
            message,
            {
                tag: `shipment-${shipmentId}`,
                data: { shipmentId },
                actions: [
                    {
                        action: 'view_shipment',
                        title: 'Xem chi tiết'
                    },
                    {
                        action: 'dismiss',
                        title: 'Đóng'
                    }
                ],
                ...options
            }
        );
    }
}

// Create singleton instance
export const notificationDisplay = new NotificationDisplay();

// Export individual functions for easier use
export const showNotification = (title, body, options) => 
    notificationDisplay.showNotification(title, body, options);

export const showSuccessNotification = (title, message, options) => 
    notificationDisplay.showSuccess(title, message, options);

export const showErrorNotification = (title, message, options) => 
    notificationDisplay.showError(title, message, options);

export const showLoginSuccessNotification = (userName) => 
    notificationDisplay.showLoginSuccess(userName);

export const showShipmentNotification = (shipmentId, message, options) => 
    notificationDisplay.showShipmentUpdate(shipmentId, message, options);